import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import FetchData from '../../../../utils/FetchData'
import style from './CVInsights.module.css'

const scoreKeys = [
  { key: 'ats_score',             label: 'ATS Score',      color: '#6ee7b7' },
  { key: 'clarity_score',         label: 'Clarity',        color: '#93c5fd' },
  { key: 'impact_score',          label: 'Impact',         color: '#fbbf24' },
  { key: 'structure_score',       label: 'Structure',      color: '#c4b5fd' },
  { key: 'quantification_score',  label: 'Quantification', color: '#f9a8d4' },
  { key: 'specialization_score',  label: 'Specialization', color: '#6ee7b7' },
  { key: 'breadth_score',         label: 'Breadth',        color: '#fdba74' },
  { key: 'consistency_score',     label: 'Consistency',    color: '#a5f3fc' },
]

const ScoreBar = ({ label, value, color, index }) => {
  const pct = Math.round((value ?? 0) * 100)
  return (
    <div className={style.barRow}>
      <div className={style.barLabel}>{label}</div>
      <div className={style.barTrack}>
        <motion.div
          className={style.barFill}
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <div className={style.barValue}>{pct}%</div>
    </div>
  )
}

const CVInsights = ({ owner }) => {
  const [insights, setInsights] = useState(undefined)
  const [cvFile, setCvFile] = useState(null)

  useEffect(() => {
    if (!owner) return
    FetchData.userInsights()
      .then((d) => {
        setInsights(d?.insights ?? null)
        setCvFile(d?.file ?? null)
      })
      .catch(() => setInsights(null))
  }, [owner])

  if (!owner) return null
  if (insights === undefined) return null
  if (insights === null) return null

  const hasScores = scoreKeys.some((s) => insights[s.key] != null)

  return (
    <motion.div
      className={style.wrapper}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className={style.header}>
        <div className={style.headerLeft}>
          <span className={style.icon}>✦</span>
          <span className={style.title}>CV Insights</span>
          {cvFile?.name && (
            <span className={style.cvFileName} title={cvFile.name}>
              {cvFile.name}
            </span>
          )}
        </div>
        {insights.seniority_estimate && (
          <span className={style.seniority}>{insights.seniority_estimate}</span>
        )}
      </div>

      {/* Scores */}
      {hasScores && (
        <div className={style.scoresSection}>
          {scoreKeys.map(({ key, label, color }, i) =>
            insights[key] != null ? (
              <ScoreBar key={key} label={label} value={insights[key]} color={color} index={i} />
            ) : null
          )}
        </div>
      )}

      {/* Key insight cards */}
      <div className={style.cards}>
        {insights.biggest_strength && (
          <div className={`${style.card} ${style.cardGreen}`}>
            <div className={style.cardLabel}>Biggest Strength</div>
            <div className={style.cardText}>{insights.biggest_strength}</div>
          </div>
        )}
        {insights.biggest_gap && (
          <div className={`${style.card} ${style.cardAmber}`}>
            <div className={style.cardLabel}>Biggest Gap</div>
            <div className={style.cardText}>{insights.biggest_gap}</div>
          </div>
        )}
        {insights.core_theme && (
          <div className={`${style.card} ${style.cardPurple}`}>
            <div className={style.cardLabel}>Core Theme</div>
            <div className={style.cardText}>{insights.core_theme}</div>
          </div>
        )}
        {insights.cv_feel && (
          <div className={`${style.card} ${style.cardBlue}`}>
            <div className={style.cardLabel}>CV Feel</div>
            <div className={style.cardText}>{insights.cv_feel}</div>
          </div>
        )}
      </div>

      {/* Strongest skills */}
      {insights.strongest_skills?.length > 0 && (
        <div className={style.skillsSection}>
          <div className={style.sectionLabel}>Strongest Skills</div>
          <div className={style.tags}>
            {insights.strongest_skills.map((s, i) => (
              <motion.span
                key={i}
                className={style.tag}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.04, duration: 0.3 }}
              >
                {s}
              </motion.span>
            ))}
          </div>
        </div>
      )}

      {/* Improvement suggestions */}
      {insights.improvement_suggestions?.length > 0 && (
        <div className={style.suggestionsSection}>
          <div className={style.sectionLabel}>Suggestions to Improve</div>
          <ul className={style.suggestions}>
            {insights.improvement_suggestions.map((s, i) => (
              <motion.li
                key={i}
                className={style.suggestion}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.06, duration: 0.35 }}
              >
                {s}
              </motion.li>
            ))}
          </ul>
        </div>
      )}

      {insights.interesting_info && (
        <div className={style.infoRow}>
          <span className={style.infoLabel}>Interesting:</span>
          <span className={style.infoText}>{insights.interesting_info}</span>
        </div>
      )}
    </motion.div>
  )
}

export default CVInsights
